import { PageSectionProps } from "@components/page-section/page-section";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { Skeleton } from "@mui/material";
import { DefaultPlatformSelector } from "@pages/settings/general-settings/default-platform-selector";
import {
  defaultProjectLaunchTargetSelector,
  fetchingSdbCompanyFlagsSelector,
} from "@store/sdb-company/sdb-company-selector";
import { fetchCompanyCommunicationSettings } from "@store/sdb-company/sdb-company-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { useEffect, useMemo } from "react";

/** Custom hook that returns the default platform settings section */
export function useDefaultPlatformSection(): PageSectionProps {
  const dispatch = useAppDispatch();
  const { isFetchingCompanyCommunicationSettings } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );
  const { isFetchingCurrentUser } = useAppSelector(fetchingUserFlagsSelector);
  const { fetchSelectedCompanyContext } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );
  const { canEditDefaultPlatform } = useHasUserValidRoleCompanyLevel();

  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  /**
   * The section should be shown if:
   * the current user has permission to view and edit this setting. Only company EAs have access
   */
  const shouldShowSection = useMemo(() => {
    // Don't show section while the required data to check the user permission is being fetched,
    // or if the workspace is Sphere XG exclusive
    if (
      isFetchingCurrentUser ||
      fetchSelectedCompanyContext === "pending" ||
      !projectLaunchSettings.shouldShowDefaultAppSetting
    ) {
      return false;
    }

    return canEditDefaultPlatform;
  }, [
    canEditDefaultPlatform,
    isFetchingCurrentUser,
    fetchSelectedCompanyContext,
    projectLaunchSettings.shouldShowDefaultAppSetting,
  ]);

  // Fetch the company communication settings
  // They are first fetch when loading the app, but if the request failed there we can
  // attempt to fetch them here too. If they were already fetched the re-fetching is skipped in the slice.
  useEffect(() => {
    void dispatch(fetchCompanyCommunicationSettings());
  }, [dispatch]);

  return {
    title: "Default Application",
    subtitle:
      "Select which application you want to use as the default to open your projects.",
    isHidden: !shouldShowSection,
    isLoading: isFetchingCompanyCommunicationSettings,
    items: [
      {
        shouldContentTakeFullWidth: true,
        content: <DefaultPlatformSelector />,
        customSkeleton: (
          <Skeleton variant="rectangular" height={"100%"} width={"100%"} />
        ),
      },
    ],
  };
}
